/**
 * In-match chat.
 *
 * Enter opens the box, Enter sends, Escape closes — the binding every RTS in
 * this lineage has used, and the one a player will try first.
 *
 * **Nothing here touches the simulation.** Chat travels beside the tick stream
 * as a relay message, so a line arriving late, early, or not at all cannot
 * desynchronise anything. See `MSG.Chat`.
 *
 * The log fades out when it is idle rather than staying up. A permanent panel
 * in the corner of an RTS is a permanent hole in the map, and the messages
 * matter for about ten seconds each.
 */

import { MAX_CHAT_LENGTH } from '../net/protocol.js'

/** How long a line stays legible after it arrives, with the box closed. */
const FADE_AFTER_MS = 9000
/** Lines kept. Older ones scroll off the top. */
const MAX_LINES = 6

export interface ChatOptions {
  root: HTMLElement
  /** Which slot the local player is, so their own lines can be marked. */
  localSlot: number
  send(text: string): void
}

export class Chat {
  private readonly panel: HTMLDivElement
  private readonly log: HTMLUListElement
  private readonly input: HTMLInputElement
  private readonly disposers: Array<() => void> = []
  private fadeTimer = 0
  private open = false

  constructor(private readonly opts: ChatOptions) {
    this.panel = document.createElement('div')
    this.panel.className = 'ba-chat'

    this.log = document.createElement('ul')
    this.log.className = 'ba-chat-log'

    this.input = document.createElement('input')
    this.input.className = 'ba-chat-input interactive'
    this.input.maxLength = MAX_CHAT_LENGTH
    this.input.placeholder = 'Say something…'
    this.input.spellcheck = false
    this.input.style.display = 'none'

    this.panel.append(this.log, this.input)
    this.opts.root.append(this.panel)

    // Capture phase, so the box opens before anything else claims the key.
    // Game bindings ignore events aimed at a text field, so once it is open
    // the two cannot fight over the keyboard.
    const onKey = (e: KeyboardEvent): void => this.onKeyDown(e)
    window.addEventListener('keydown', onKey, true)
    this.disposers.push(() => window.removeEventListener('keydown', onKey, true))
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (e.code !== 'Enter' && e.code !== 'NumpadEnter' && e.code !== 'Escape') return

    if (!this.open) {
      // Enter with the box shut opens it. Escape with the box shut belongs to
      // the game — it cancels placement and clears the selection.
      if (e.code === 'Escape') return
      if (isTyping(e.target)) return
      e.preventDefault()
      e.stopPropagation()
      this.show()
      return
    }

    e.preventDefault()
    e.stopPropagation()
    if (e.code === 'Escape') {
      this.hide()
      return
    }
    const text = this.input.value
    this.input.value = ''
    this.hide()
    if (text.trim().length > 0) this.opts.send(text)
  }

  private show(): void {
    this.open = true
    this.input.style.display = ''
    this.panel.classList.add('open')
    this.panel.classList.remove('faded')
    window.clearTimeout(this.fadeTimer)
    this.input.focus()
  }

  private hide(): void {
    this.open = false
    this.input.style.display = 'none'
    this.panel.classList.remove('open')
    this.input.blur()
    this.scheduleFade()
  }

  /** Print a line. Called for every player including this one — see `sendChat`. */
  append(slot: number, text: string): void {
    const li = document.createElement('li')
    const who = document.createElement('span')
    who.className = `ba-chat-who slot${Math.max(0, slot) % 4}`
    who.textContent = slot === this.opts.localSlot ? 'You' : `Player ${slot + 1}`
    const body = document.createElement('span')
    // textContent, never innerHTML: this is the one string on screen that
    // another person wrote.
    body.textContent = text
    li.append(who, body)
    this.log.append(li)
    while (this.log.childElementCount > MAX_LINES) this.log.firstElementChild?.remove()

    this.panel.classList.remove('faded')
    this.scheduleFade()
  }

  /** A system line: joins, drops, and the "press Enter" nudge. */
  note(text: string): void {
    const li = document.createElement('li')
    li.className = 'note'
    li.textContent = text
    this.log.append(li)
    while (this.log.childElementCount > MAX_LINES) this.log.firstElementChild?.remove()
    this.panel.classList.remove('faded')
    this.scheduleFade()
  }

  private scheduleFade(): void {
    window.clearTimeout(this.fadeTimer)
    if (this.open) return
    this.fadeTimer = window.setTimeout(() => this.panel.classList.add('faded'), FADE_AFTER_MS)
  }

  dispose(): void {
    window.clearTimeout(this.fadeTimer)
    for (const off of this.disposers) off()
    this.panel.remove()
  }
}

/** Whether a key event is somebody typing into a field rather than playing. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable
}
